import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import { Toaster } from "sonner";
import { ConfirmProvider } from "@/components/ui/confirm-dialog";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AppLayout } from "@/components/layout/AppLayout";
import { AuthBootstrap } from "@/components/auth/AuthBootstrap";
import { ImpersonationBanner } from "@/components/auth/ImpersonationBanner";
import { ProjectSync } from "@/components/auth/ProjectSync";
import { RequireAuth } from "@/components/auth/RequireAuth";
import LoginPage from "@/pages/LoginPage";
import SetupPasswordPage from "@/pages/SetupPasswordPage";
import AccountPage from "@/pages/AccountPage";
import ProjectsPage from "@/pages/ProjectsPage";
import DashboardPage from "@/pages/DashboardPage";
import CompaniesPage from "@/pages/CompaniesPage";
import CompanyDetailPage from "@/pages/CompanyDetailPage";
import ContactsPage from "@/pages/ContactsPage";
import EngineControlPage from "@/pages/EngineControlPage";
import JobRunsPage from "@/pages/JobRunsPage";
import SettingsPage from "@/pages/SettingsPage";
import TasksPage from "@/pages/TasksPage";
import CalendarPage from "@/pages/CalendarPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import NotificationsPage from "@/pages/NotificationsPage";
import CallCenterPage from "@/pages/CallCenterPage";
import EnrichmentPage from "@/pages/EnrichmentPage";
import EmailLayout, { EmailIndexRedirect } from "@/pages/email/EmailLayout";
import CampaignsPage from "@/pages/email/CampaignsPage";
import OutreachPage from "@/pages/email/OutreachPage";
import CampaignWorkspace, { WorkspaceIndexRedirect } from "@/pages/email/CampaignWorkspace";
import SequenceTab from "@/pages/email/SequenceTab";
import LeadsTab from "@/pages/email/LeadsTab";
import LaunchTab from "@/pages/email/LaunchTab";
import PerformanceTab from "@/pages/email/PerformanceTab";
import PilotReviewTab from "@/pages/email/PilotReviewTab";

export default function App() {
  return (
    <ErrorBoundary>
      <ConfirmProvider>
        <BrowserRouter>
          <AuthBootstrap />
          <ImpersonationBanner />
          <Routes>
          <Route path="/" element={<Navigate to="/projects" replace />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/setup-password" element={<SetupPasswordPage />} />
          <Route
            path="/projects"
            element={
              <RequireAuth>
                <ProjectsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/projects/:slug"
            element={
              <RequireAuth>
                <ProjectSync />
                <AppLayout />
              </RequireAuth>
            }
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="companies" element={<CompaniesPage />} />
            <Route path="companies/:id" element={<CompanyDetailPage />} />
            <Route path="contacts" element={<ContactsPage />} />
            <Route path="call-center" element={<CallCenterPage />} />
            <Route path="enrichment" element={<EnrichmentPage />} />
            {/* Email: Campaigns | Outreach; a campaign opens in its workspace. Former
                URLs (?tab=…, ?campaign=…, the campaign page, ?view=…) redirect. */}
            <Route path="email" element={<EmailLayout />}>
              <Route index element={<EmailIndexRedirect />} />
              <Route path="campaigns" element={<CampaignsPage />} />
              <Route path="outreach" element={<OutreachPage />} />
              <Route path="*" element={<EmailIndexRedirect unknown />} />
            </Route>
            <Route path="email/campaigns/:campaignId" element={<CampaignWorkspace />}>
              <Route index element={<WorkspaceIndexRedirect />} />
              <Route path="sequence" element={<SequenceTab />} />
              <Route path="leads" element={<LeadsTab />} />
              <Route path="launch" element={<LaunchTab />} />
              <Route path="performance" element={<PerformanceTab />} />
              <Route path="review" element={<PilotReviewTab />} />
              <Route path="*" element={<WorkspaceIndexRedirect />} />
            </Route>
            <Route path="tasks" element={<TasksPage />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="engine" element={<EngineControlPage />} />
            <Route path="engine/:jobId/runs" element={<JobRunsPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="account" element={<AccountPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/projects" replace />} />
          </Routes>
          <Toaster position="bottom-right" richColors closeButton />
        </BrowserRouter>
      </ConfirmProvider>
    </ErrorBoundary>
  );
}
